import { AccountStatus, GymEnvironment, IPrice, SupportedCurrency } from 'fitbeat.models';
import { AccountStatusManager } from '../accountstatus';

type StatusCurrencyPriceMap = Map<AccountStatus, Map<SupportedCurrency, string>>;

export class CountryPlanMappingService {
  public static buildStatusCurrencyPlanMap(
    prices: IPrice[],
    accountStatusManager: AccountStatusManager,
    gymEnvironment: GymEnvironment,
    logger: any,
  ): StatusCurrencyPriceMap {
    const statusCurrencyMap = new Map<AccountStatus, Map<SupportedCurrency, string>>();
    for (const price of prices) {
      const defaultPrice = price.metadata.defaultPriceForAccountStatus;
      if (defaultPrice) {
        const accountStatus = accountStatusManager.getStatusFromPlanAndProductIds(
          price.priceId,
          price.product.id,
          gymEnvironment,
        );

        if (accountStatus) {
          const currency = price.currency as SupportedCurrency;
          const existingCurrencyPlanMap = statusCurrencyMap.get(accountStatus);
          if (existingCurrencyPlanMap) {
            const previouslySavedPriceId = existingCurrencyPlanMap.get(currency);

            if (previouslySavedPriceId) {
              logger.error(`Multiple default values for currency ${currency} on account status ${
                accountStatus} using lowest value price`);

              const previouslySavedPrice = prices.find((plan) => plan.priceId === previouslySavedPriceId);
              if (previouslySavedPrice!.unitAmount > price.unitAmount) {
                existingCurrencyPlanMap.set(currency as SupportedCurrency, price.priceId);
              }

            } else {
              existingCurrencyPlanMap.set(currency as SupportedCurrency, price.priceId);
            }
          } else {
            const newCurrencyPlanMap = new Map<SupportedCurrency, string>([[currency, price.priceId]]);
            statusCurrencyMap.set(accountStatus, newCurrencyPlanMap);
          }
        }
      }
    }
    logger.info(statusCurrencyMap);
    return statusCurrencyMap;
  }

  private readonly countryCurrencyMap = {
    US: 'usd',
    AU: 'aud',
  };
  private readonly defaultCurrency: SupportedCurrency = 'usd';

  constructor(
    private readonly statusCurrencyPlanMap: StatusCurrencyPriceMap,
  ) {
  }

  public getPlanIdForStatusFromCountry(accountStatus: AccountStatus, countryCode: string): string {
    const currencyForCountry = this.getCountryCurrency(countryCode);
    return this.getPlanIdForStatusFromCurrency(accountStatus, currencyForCountry);
  }

  public getPlanIdForStatusFromCurrency(
    accountStatus: AccountStatus,
    currency = this.defaultCurrency,
  ): string {
    const pricesForStatus = this.getPricesForStatus(accountStatus);
    const planId = pricesForStatus.get(currency);
    if (!planId) {
      throw new Error(`No mapping present for account status ${accountStatus} using currency ${currency}`);
    }

    return planId;
  }

  public getPlanIdsForAllCurrenciesAndStatus(accountStatus: AccountStatus): string[] {
    const pricesForStatus = this.getPricesForStatus(accountStatus);
    return Array.from(pricesForStatus, ([_, planId]) => planId);
  }

  private getPricesForStatus(accountStatus: AccountStatus) {
    const pricesForStatus = this.statusCurrencyPlanMap.get(accountStatus);
    if (!pricesForStatus) {
      throw new Error(`No mapping present for account status ${accountStatus}`);
    }
    return pricesForStatus;
  }

  private getCountryCurrency(countryCode: string): SupportedCurrency {
    return this.countryCurrencyMap[countryCode] || this.defaultCurrency;
  }
}
